import { useState, useMemo, useCallback } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import {
  getEligibilityAssessmentStatusLabel,
  ELIGIBILITY_ASSESSMENT_STATUS,
} from 'src/pages/facesheet/_id/eligibility/utils';

const PAGE_SIZE = 10;

export const STATUS_FILTER_OPTIONS = [
  { label: 'Draft', value: ELIGIBILITY_ASSESSMENT_STATUS.DRAFT },
  { label: 'Complete', value: ELIGIBILITY_ASSESSMENT_STATUS.COMPLETE },
  { label: 'Declined', value: ELIGIBILITY_ASSESSMENT_STATUS.DECLINED },
  { label: 'Outreach Failed', value: 'outreach_failed' },
];

const getEffectiveStatus = (assessment) => {
  const label = getEligibilityAssessmentStatusLabel(
    assessment.status,
    assessment.outreach_status,
  );
  return label === 'Outreach Failed' ? 'outreach_failed' : label;
};

const getSortableDate = (assessment) => {
  const isDraft = assessment.status === ELIGIBILITY_ASSESSMENT_STATUS.DRAFT;
  const dateStr = isDraft ? assessment.updated_at : assessment.status_at;
  return dateStr ? new Date(dateStr).getTime() : 0;
};

const getAssessmentMonthYear = (assessment) => {
  const isDraft = assessment.status === ELIGIBILITY_ASSESSMENT_STATUS.DRAFT;
  const dateStr = isDraft ? assessment.updated_at : assessment.status_at;
  if (!dateStr) return null;
  return moment(dateStr).format('MMMM YYYY');
};

const getAssessmentName = (assessment) => get(assessment, 'template.name', '') || 'Eligibility Assessment';
const getSubmitterName = (assessment) => get(assessment, 'facilitator.full_name', '') || 'N/A';
const getOrganizationName = (assessment) => get(assessment, 'provider.name', '');

const DEFAULT_FILTERS = {
  status: [],
  organization: [],
  name: [],
  submitter: [],
  date: [],
};

const FILTER_ACCESSORS = {
  status: getEffectiveStatus,
  organization: getOrganizationName,
  name: getAssessmentName,
  submitter: getSubmitterName,
  date: getAssessmentMonthYear,
};

const buildUniqueOptions = (items, accessor) => {
  const unique = Array.from(new Set(items.map(accessor).filter(Boolean)));
  return unique.map((val) => ({ label: val, value: val }));
};

const buildOptions = (assessments, accessor) => (
  assessments ? buildUniqueOptions(assessments, accessor) : []
);

const buildFilterOptions = (assessments) => ({
  orgFilterOptions: buildOptions(assessments, getOrganizationName),
  nameFilterOptions: buildOptions(assessments, getAssessmentName),
  submitterFilterOptions: buildOptions(assessments, getSubmitterName),
  dateFilterOptions: buildOptions(assessments, getAssessmentMonthYear),
});

const hasActiveFilters = (filters) => (
  Object.values(filters).some((filterValues) => filterValues.length > 0)
);

const applyFilter = (items, [filterKey, values]) => {
  const accessor = FILTER_ACCESSORS[filterKey];
  if (!values.length || !accessor) return items;

  return items.filter((assessment) => values.includes(accessor(assessment)));
};

const applyFilters = (assessments, filters) => {
  const filtersList = Object.entries(filters);
  return filtersList.reduce(applyFilter, [...assessments]);
};

const sortAssessments = (assessments, sortDirection) => assessments.sort((a, b) => {
  const dateA = getSortableDate(a);
  const dateB = getSortableDate(b);
  return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
});

const filterAndSortAssessments = (assessments, filters, sortDirection) => {
  const assessmentList = assessments || [];
  const filtered = applyFilters(assessmentList, filters);
  return sortAssessments(filtered, sortDirection);
};

const getPaginatedAssessments = (assessments, page) => assessments.slice(
  (page - 1) * PAGE_SIZE,
  page * PAGE_SIZE,
);

const getNextPage = (page, totalPages) => (page < totalPages ? page + 1 : null);
const getPrevPage = (page) => (page > 1 ? page - 1 : null);

const buildPaging = (page, totalCount, totalPages) => ({
  current_page: page,
  next_page: getNextPage(page, totalPages),
  prev_page: getPrevPage(page),
  total_count: totalCount,
  total_pages: totalPages,
  per: PAGE_SIZE,
});

const getNextSortDirection = (currentSortBy, currentSortDirection, column, direction) => {
  if (direction) return direction;
  return currentSortBy === column && currentSortDirection === 'asc' ? 'desc' : 'asc';
};

const updateFilters = (currentFilters, filterKey, values) => {
  if (!Object.prototype.hasOwnProperty.call(currentFilters, filterKey)) return currentFilters;
  return {
    ...currentFilters,
    [filterKey]: values,
  };
};

export const useAssessmentFiltering = (assessments) => {
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const hasFilters = hasActiveFilters(filters);
  const filterOptions = useMemo(() => buildFilterOptions(assessments), [assessments]);
  const filteredAndSorted = useMemo(
    () => filterAndSortAssessments(assessments, filters, sortDirection),
    [assessments, filters, sortDirection],
  );
  const totalCount = filteredAndSorted.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginatedAssessments = getPaginatedAssessments(filteredAndSorted, page);
  const paging = buildPaging(page, totalCount, totalPages);

  const onSortChange = useCallback((column, direction) => {
    const newDirection = getNextSortDirection(sortBy, sortDirection, column, direction);
    setSortBy(column);
    setSortDirection(newDirection);
    setPage(1);
  }, [sortBy, sortDirection]);

  const onFiltersChange = useCallback((filterKey, values) => {
    setFilters((currentFilters) => updateFilters(currentFilters, filterKey, values));
    setPage(1);
  }, []);

  const onNextClick = useCallback(() => setPage((p) => Math.min(p + 1, totalPages)), [totalPages]);
  const onPrevClick = useCallback(() => setPage((p) => Math.max(p - 1, 1)), []);

  return {
    sortBy,
    sortDirection,
    hasFilters,
    paginatedAssessments,
    totalCount,
    paging,
    orgFilterOptions: filterOptions.orgFilterOptions,
    nameFilterOptions: filterOptions.nameFilterOptions,
    submitterFilterOptions: filterOptions.submitterFilterOptions,
    dateFilterOptions: filterOptions.dateFilterOptions,
    onSortChange,
    onFiltersChange,
    onNextClick,
    onPrevClick,
  };
};
